import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
const getRangeValues = (valueLeft, valueRight) => {
    const finalLeftValue = valueRight <= valueLeft ? valueRight : valueLeft;
    const finalRightValue = valueLeft >= valueRight ? valueLeft : valueRight;
    return [finalLeftValue, finalRightValue];
};
const RangeSlider = props => {
    const { minValue, maxValue, valueLabels, step, leftValue, rightValue, className, disabled, onChange, onDragEnd } = props, remainingProps = __rest(props, ["minValue", "maxValue", "valueLabels", "step", "leftValue", "rightValue", "className", "disabled", "onChange", "onDragEnd"]);
    const [activeLeft, setActiveLeft] = useState(false);
    const [activeRight, setActiveRight] = useState(false);
    const [valueLeft, setValueLeft] = useState(leftValue ? leftValue : minValue);
    const [valueRight, setValueRight] = useState(rightValue ? rightValue : maxValue);
    const [isChangingLeft, setIsChangingLeft] = useState(false);
    const [isChangingRight, setIsChangingRight] = useState(false);
    useEffect(() => {
        if (valueLeft >= minValue && valueLeft <= maxValue && valueRight >= minValue && valueRight <= maxValue) {
            setValueLeft(valueLeft);
            setValueRight(valueRight);
        }
    }, [valueLeft, valueRight, minValue, maxValue]);
    const changeValues = newValues => {
        const newValueLeft = newValues.valueLeft ? newValues.valueLeft : valueLeft;
        const newValueRight = newValues.valueRight ? newValues.valueRight : valueRight;
        setValueLeft(newValueLeft);
        setValueRight(newValueRight);
        setIsChangingLeft(!!newValues.valueLeft);
        setIsChangingRight(!!newValues.valueRight);
        const [newLeftValue, newRightValue] = getRangeValues(newValueLeft, newValueRight);
        onChange(newLeftValue, newRightValue);
    };
    const handleChangeValueLeft = event => changeValues({ valueLeft: parseInt(event.target.value, 10) });
    const handleChangeValueRight = event => changeValues({ valueRight: parseInt(event.target.value, 10) });
    const onMouseDownLeft = () => {
        setIsChangingLeft(true);
        setIsChangingRight(false);
    };
    const onMouseDownRight = () => {
        setIsChangingLeft(false);
        setIsChangingRight(true);
    };
    const getTrackMiddlePosition = targetWidth => {
        const trackValueLeftRelative = (valueLeft - minValue) / (maxValue - minValue);
        const trackValueRightRelative = (valueRight - minValue) / (maxValue - minValue);
        const trackMiddleRelative = (trackValueLeftRelative + trackValueRightRelative) / 2;
        return trackMiddleRelative * targetWidth;
    };
    const onMouseMoveLeft = event => {
        if (!isChangingLeft) {
            const target = event.target.getBoundingClientRect();
            const mouseHover = event.clientX - target.left;
            const targetWidth = event.target.offsetWidth;
            const trackMiddlePosition = getTrackMiddlePosition(targetWidth);
            if (valueRight > valueLeft && mouseHover > trackMiddlePosition) {
                setActiveLeft(false);
                setActiveRight(true);
            }
            if (valueRight < valueLeft && mouseHover < trackMiddlePosition) {
                setActiveLeft(false);
                setActiveRight(true);
            }
        }
    };
    const onMouseMoveRight = event => {
        if (!isChangingRight) {
            const target = event.target.getBoundingClientRect();
            const mouseHover = event.clientX - target.left;
            const targetWidth = event.target.offsetWidth;
            const trackMiddlePosition = getTrackMiddlePosition(targetWidth);
            if (valueRight > valueLeft && mouseHover < trackMiddlePosition) {
                setActiveLeft(true);
                setActiveRight(false);
            }
            if (valueRight < valueLeft && mouseHover > trackMiddlePosition) {
                setActiveLeft(true);
                setActiveRight(false);
            }
        }
    };
    const onMouseUpLeft = () => {
        setIsChangingLeft(false);
        setIsChangingRight(false);
        const [newLeftValue, newRightValue] = getRangeValues(valueLeft, valueRight);
        onDragEnd(newLeftValue, newRightValue);
    };
    const onMouseUpRight = () => {
        setIsChangingLeft(false);
        setIsChangingRight(false);
        const [newLeftValue, newRightValue] = getRangeValues(valueLeft, valueRight);
        onDragEnd(newLeftValue, newRightValue);
    };
    const [finalLeftValue, finalRightValue] = getRangeValues(valueLeft, valueRight);
    const trackWidth = (100 * (finalRightValue - finalLeftValue)) / (maxValue - minValue);
    const trackLeft = (100 * (finalLeftValue - minValue)) / (maxValue - minValue);
    const wrapperClassNames = classNames('range-slider', disabled && 'disabled', valueLabels && 'show-value-labels', className && className);
    return (_jsxs("div", Object.assign({}, remainingProps, { className: wrapperClassNames }, { children: [_jsx("div", { className: 'range-whole-track' }), _jsx("div", Object.assign({ className: 'range-track', style: { width: `${trackWidth}%`, left: `${trackLeft}%` } }, { children: valueLabels && (_jsxs("div", { children: [_jsx("div", Object.assign({ className: 'range-value' }, { children: finalLeftValue })), _jsx("div", Object.assign({ className: 'range-value' }, { children: finalRightValue }))] })) })), _jsx("input", { className: `${isChangingLeft ? 'changing' : ''}`, style: { zIndex: activeLeft ? '6' : '5' }, value: valueLeft, min: minValue, max: maxValue, step: step, type: 'range', onChange: handleChangeValueLeft, onMouseDown: onMouseDownLeft, onMouseMove: onMouseMoveLeft, onMouseUp: onMouseUpLeft }), _jsx("input", { className: `${isChangingRight ? 'changing' : ''}`, style: { zIndex: activeRight ? '6' : '5' }, value: valueRight, min: minValue, max: maxValue, step: step, type: 'range', onChange: handleChangeValueRight, onMouseDown: onMouseDownRight, onMouseMove: onMouseMoveRight, onMouseUp: onMouseUpRight })] })));
};
RangeSlider.defaultProps = {
    step: 1,
    minValue: 0,
    maxValue: Number.MAX_VALUE,
    valueLabels: false,
    disabled: false,
    onChange: () => { },
    onDragEnd: () => { },
};
RangeSlider.propTypes = {
    leftValue: PropTypes.number,
    rightValue: PropTypes.number,
    minValue: PropTypes.number.isRequired,
    maxValue: PropTypes.number.isRequired,
    valueLabels: PropTypes.bool,
    step: PropTypes.number,
    onChange: PropTypes.func,
    onDragEnd: PropTypes.func,
    className: PropTypes.string,
    disabled: PropTypes.bool,
};
export default RangeSlider;
